/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import React, { useId, type ReactNode } from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";

export const Switch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return (
        <SwitchPrimitives.Root
            className={cn(
                // this gives the switch a line-height of 24px that matches the height of our base font size
                "my-0.5",
                "group peer inline-flex h-[20px] w-[36px] shrink-0 cursor-pointer items-center",
                "rounded-2xl transition-colors",
                "focus-visible:ring-offset-background",
                "focus-visible:ring-kumquat-ripe focus-visible:data-[state=checked]:bg-kumquat-gradient-muted",
                "disabled:cursor-default disabled:bg-surface-labels",
                "data-[state=checked]:disabled:bg-kumquat-gradient-muted",
                // checked state colors
                "data-[state=checked]:bg-kumquat-gradient",
                // unchecked state colors
                "data-[state=unchecked]:bg-surface-labels",
                className,
            )}
            {...props}
            ref={ref}
        >
            <SwitchPrimitives.Thumb
                className={cn(
                    "pointer-events-none block h-[16px] w-[16px] rounded-full",
                    "bg-surface-primary ring-0 drop-shadow",
                    "group-disabled:bg-surface-tertiary",
                    // Positioning
                    "transition-transform data-[state=checked]:translate-x-[17px] data-[state=unchecked]:translate-x-[3px]",
                )}
            />
        </SwitchPrimitives.Root>
    );
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export type SwitchInputFieldProps = {
    id?: string;
    label: ReactNode;
    description?: ReactNode;
} & React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>;

export const SwitchInputField = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchInputFieldProps>(
    ({ className, checked, onCheckedChange, label, description, ...props }, ref) => {
        const generatedId = useId();
        const switchProps = {
            ...props,
            className: "",
            id: props.id ?? generatedId,
        };
        return (
            <div className={cn("flex gap-4", className)}>
                <Switch checked={checked} onCheckedChange={onCheckedChange} {...switchProps} ref={ref} />
                <div className="flex flex-col">
                    <label className="cursor-pointer font-bold" htmlFor={switchProps.id}>
                        {label}
                    </label>
                    <Text muted>{description}</Text>
                </div>
            </div>
        );
    },
);

SwitchInputField.displayName = "SwitchInputField";
